import React, { useState, useEffect } from 'react';
import { useNavigationUtils } from '../utils/navigationUtils';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Chip,
  Alert,
  Snackbar,
  Paper
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';

import ProfileService from '../services/ProfileService';
import CompanyProfileService from '../services/CompanyProfileService';
import { useAuth } from '../services/AuthService';
import { usePostHog } from '@posthog/react';
import { completeCompanySchema } from '../validation/onboardingSchema';

const industryOptions = [
  { label: 'Construction', value: 'construction' },
  { label: 'Cleaning Services', value: 'cleaning' },
  { label: 'Labour Hire', value: 'labourhire' },
  { label: 'Landscaping', value: 'landscaping' },
  { label: 'Warehouse/Logistics', value: 'warehouse' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Other', value: 'other' }
];

// hiring frequency removed per TODO

const communicationChannelOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone Call', value: 'phone' },
  { label: 'SMS', value: 'sms' },
  { label: 'WhatsApp', value: 'whatsapp' },
];

const FieldSet = ({ title, helper, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 3, md: 4 },
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper'
    }}
  >
    <Stack spacing={1} mb={3}>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      {helper && (
        <Typography variant="body2" color="text.secondary">
          {helper}
        </Typography>
      )}
    </Stack>
    {children}
  </Paper>
);

const twoColumnLayout = {
  display: 'grid',
  gap: { xs: 2, md: 3 },
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
};

const fullWidthField = {
  gridColumn: { xs: 'auto', md: '1 / -1' }
};

const validateABN = (abn) => {
  if (!abn) return true;
  const cleanABN = abn.replace(/[^0-9]/g, '');
  if (cleanABN.length !== 11) return false;
  
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = 0;
  
  for (let i = 0; i < 11; i++) {
    let digit = parseInt(cleanABN[i], 10);
    if (i === 0)
      digit = digit - 1;
    sum += digit * weights[i];
  }
  return sum % 89 === 0;
};

const CompanyProfileManagement = () => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const { navigateBackToDashboard } = useNavigationUtils();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const methods = useForm({
    mode: 'onChange',
      defaultValues: {
      userType: 'company',
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      company: {
        companyName: '',
        abn: '',
        industry: '',
          
        companyPhone: '',
        companyEmail: '',
        preferredCommunication: [],
      },
    },
  });

  const { reset, handleSubmit, formState: { errors } } = methods;
  const profileErrors = errors.profile || {};
  const companyErrors = errors.company || {};

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        if (!user) {
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const userId = user?.sub;
        const profile = await new ProfileService().getRecordsByUserId(userId);
        const companyProfile = await new CompanyProfileService().getRecordsByUserId(userId);

            const mergedData = {
          userType: 'company',
          profile: {
            firstName: profile?.firstName || user?.given_name || '',
            lastName: profile?.lastName || user?.family_name || '',
            email: profile?.email || user?.email || '',
            phone: profile?.phone || user?.phone_number || '',
          },
          company: {
            companyName: companyProfile?.companyName || '',
            abn: companyProfile?.abn || '',
            industry: companyProfile?.industry || '',
                
            companyPhone: companyProfile?.companyPhone || user?.phone_number || '',
            companyEmail: companyProfile?.companyEmail || user?.email || '',
            preferredCommunication: companyProfile?.preferredCommunication || [],
          },
        };

        reset(mergedData);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading profile data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [user, reset]);

  const onSubmit = async (data) => {
    setIsSaving(true);
    const userId = user?.sub;
    
    try {
      // Validate the data
      await completeCompanySchema.validate(data, { abortEarly: false });
      
      console.log('Saving profile data:', { userId, ...data });

      // Data is already split into logical structures — no mapping needed
      await new ProfileService().createOrUpdateRecord({ userId, userType: data.userType, ...data.profile, isCompleted: true });
      await new CompanyProfileService().createOrUpdateRecord({ userId, ...data.company });

      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      posthog?.capture('profile_updated', { user_type: 'company' });
    } catch (error) {
      console.error('Error saving profile:', error);
      if (error.name === 'ValidationError') {
        // Handle validation errors
        error.inner.forEach((err) => {
          methods.setError(err.path, {
            type: 'manual',
            message: err.message,
          });
        });
        setSnackbar({
          open: true,
          message: 'Please fix the validation errors',
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error saving profile. Please try again.',
          severity: 'error'
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigateBackToDashboard();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading || !user) {
    return (
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Loading your profile...
          </Typography>
        </Box>
    );
  }

  return (
      <FormProvider {...methods}>
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: 'background.default',
            py: { xs: 4, md: 6 },
            px: 2
          }}
        >
          <Container maxWidth="md">
            <Stack spacing={{ xs: 3, md: 4 }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                variant="text"
                color="inherit"
                sx={{
                  alignSelf: { xs: 'stretch', md: 'flex-start' },
                  justifyContent: 'flex-start',
                  width: 'fit-content'
                }}
              >
                Back to dashboard
              </Button>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundImage: (theme) => theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, hsla(145, 72%, 40%, 0.1), hsla(38, 85%, 55%, 0.08))`
                    : `linear-gradient(135deg, ${theme.palette.primary[50]}, ${theme.palette.secondary[50]})`
                }}
              >
                <Stack spacing={1} alignItems="center">
                  <Stack spacing={1}>
                    <Typography variant="h6" component="h1" fontWeight={700}>
                      Company Profile
                    </Typography>
                  </Stack>

                </Stack>
              </Paper>

              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={{ xs: 3, md: 4 }}>
                  <FieldSet
                    title="Basic information"
                    helper="Tell us who will coordinate with candidates and partners."
                  >
                    <Box sx={twoColumnLayout}>
                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="profile.firstName"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                placeholder="Enter your first name"
                                fullWidth
                                label="First name"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!profileErrors.firstName}
                                helperText={profileErrors.firstName?.message}
                                required
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="profile.lastName"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                placeholder="Enter your last name"
                                fullWidth
                                label="Last name"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!profileErrors.lastName}
                                helperText={profileErrors.lastName?.message}
                                required
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="profile.email"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="email"
                                placeholder="your.email@example.com"
                                fullWidth
                                label="Email address"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!profileErrors.email}
                                helperText={profileErrors.email?.message}
                                required
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <EmailIcon />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="profile.phone"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="tel"
                                placeholder="04XX XXX XXX"
                                fullWidth
                                label="Phone number"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!profileErrors.phone}
                                helperText={profileErrors.phone?.message}
                                required
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PhoneIcon />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Box>
                    </Box>
                  </FieldSet>

                  <FieldSet
                    title="Company information"
                    helper="Share your ABN, hiring cadence, and preferred communication channels."
                  >
                    <Box sx={twoColumnLayout}>
                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="company.companyName"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                placeholder="Enter your company name"
                                fullWidth
                                label="Company name"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!companyErrors.companyName}
                                helperText={companyErrors.companyName?.message}
                                required
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="company.abn"
                            control={methods.control}
                            rules={{ validate: (value) => !value || validateABN(value) || 'Please enter a valid ABN' }}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                placeholder="Company ABN"
                                fullWidth
                                label="ABN"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!error}
                                helperText={error ? error.message : ''}
                                required
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box>
                        <FormControl fullWidth error={!!companyErrors.industry}>
                          <InputLabel id="industry-label">Industry *</InputLabel>
                          <Controller
                            name="company.industry"
                            control={methods.control}
                            render={({ field }) => (
                              <Select sx={{ bgcolor: 'background.input' }} {...field} labelId="industry-label" label="Industry *">
                                {industryOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          {companyErrors.industry && (
                            <FormHelperText>{companyErrors.industry.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>

                      {/* Hiring frequency question removed */}

                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="company.companyPhone"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                placeholder="Company phone number"
                                fullWidth
                                label="Company phone number"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!companyErrors.companyPhone}
                                helperText={companyErrors.companyPhone?.message}
                                required
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PhoneIcon />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box>
                        <FormControl fullWidth>
                          <Controller
                            name="company.companyEmail"
                            control={methods.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                placeholder="Company email"
                                fullWidth
                                label="Company email"
                                variant="outlined"
                                sx={{ bgcolor: 'background.input' }}
                                error={!!companyErrors.companyEmail}
                                helperText={companyErrors.companyEmail?.message}
                                required
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <EmailIcon />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Box>

                      <Box sx={fullWidthField}>
                        <FormControl fullWidth error={!!companyErrors.preferredCommunication}>
                          <InputLabel id="preferredCommunication-label">Preferred communication channels *</InputLabel>
                          <Controller
                            name="company.preferredCommunication"
                            control={methods.control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                labelId="preferredCommunication-label"
                                multiple
                                label="Preferred communication channels *"
                                sx={{ bgcolor: 'background.input' }}
                                renderValue={(selected) => (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                      const option = communicationChannelOptions.find((opt) => opt.value === value);
                                      return <Chip key={value} label={option ? option.label : value} color="primary" variant="outlined" />;
                                    })}
                                  </Box>
                                )}
                              >
                                {communicationChannelOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          <FormHelperText>
                            {companyErrors.preferredCommunication
                              ? companyErrors.preferredCommunication.message
                              : 'Select every channel applicants can use to contact you'}
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    </Box>
                  </FieldSet>
                </Stack>

                <Stack alignItems="center" mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSaving}
                    startIcon={<SaveIcon />}
                    sx={{
                      minWidth: 220,
                      py: 1.5,
                      borderRadius: 999,
                      textTransform: 'none'
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save profile'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Container>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </FormProvider>
  );
};

export default CompanyProfileManagement;